import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { compressImageTo1MB, fmtKB } from "@/lib/imageCompress";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Camera, X, ChartLineUp, UploadSimple, Receipt } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button, IconButton } from "@/components/ui-kit/Button";
import { fmtYmd } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";
import VehicleQrField from "@/components/VehicleQrField";

const PAYMENT_OPTIONS = [
    { value: "corporate_card", label: "法人カード" },
    { value: "fleet_card", label: "燃料カード" },
    { value: "personal_advance", label: "個人立替" },
    { value: "cash", label: "現金" },
    { value: "other", label: "その他" },
];
const QUICK_LITERS = [10, 20, 30, 40, 50];

const emptyForm = {
    id: null,
    vehicle_id: "",
    refueled_at: new Date().toISOString().slice(0, 10),
    refueled_time: "",
    odometer_km: "",
    liters: "",
    unit_price_jpy: "",
    total_jpy: "",
    fuel_type: "",
    station_name: "",
    payment_method: "",
    receipt_image_url: "",
    notes: "",
};

export default function Fuel() {
    const { can, user } = useAuth();
    const isStaff = user?.role === "staff";
    const canCreate = can("fuel.create");
    const canUpdate = can("fuel.update");
    const canDelete = can("fuel.delete");
    const canImport = can("fuel.import");
    const canAnalytics = can("fuel.analytics");

    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [summary, setSummary] = useState({ trips: 0, liters: 0, cost: 0, avg_kmpl: 0, days: 30 });
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [csvOpen, setCsvOpen] = useState(false);
    const [trendOpen, setTrendOpen] = useState(false);
    // Receipt viewer modal — list response no longer carries the (multi-MB)
    // base64 image so we lazy-fetch the single record when user taps the
    // 領収書 button. {open: bool, src: data-url, busy: bool, log: meta}
    const [receiptModal, setReceiptModal] = useState({ open: false, src: "", busy: false, log: null });

    const openReceipt = async (log) => {
        setReceiptModal({ open: true, src: "", busy: true, log });
        try {
            const { data } = await api.get(`/fuel-logs/${log.id}`);
            if (data?.receipt_image_url) {
                setReceiptModal({ open: true, src: data.receipt_image_url, busy: false, log: data });
            } else {
                toast.error("この記録に領収書が登録されていません");
                setReceiptModal({ open: false, src: "", busy: false, log: null });
            }
        } catch (e) {
            toast.error("領収書の取得に失敗しました");
            setReceiptModal({ open: false, src: "", busy: false, log: null });
        }
    };
    const fileInputRef = useRef(null);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (filterVehicle) params.set("vehicle_id", filterVehicle);
            if (filterFrom) params.set("date_from", filterFrom);
            if (filterTo) params.set("date_to", filterTo);
            const qs = params.toString();
            const [f, v, s] = await Promise.all([
                api.get(`/fuel-logs${qs ? `?${qs}` : ""}`),
                api.get("/vehicles"),
                api.get(`/fuel-logs/summary?days=30${filterVehicle ? `&vehicle_id=${filterVehicle}` : ""}`),
            ]);
            setList(f.data);
            setVehicles(v.data);
            setSummary(s.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "読込失敗");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterVehicle, filterFrom, filterTo]);

    const openNew = () => { setForm(emptyForm); setOpen(true); };
    const openEdit = async (f) => {
        // The list-version of the record doesn't include the base64 receipt
        // image. Re-fetch the full record so the existing receipt survives an
        // edit-then-save round trip.
        let full = f;
        if (f.has_receipt) {
            try {
                const { data } = await api.get(`/fuel-logs/${f.id}`);
                full = data;
            } catch (e) {
                /* fall back to list copy */
            }
        }
        setForm({
            id: full.id,
            vehicle_id: full.vehicle_id,
            refueled_at: full.refueled_at,
            refueled_time: full.refueled_time || "",
            odometer_km: full.odometer_km ?? "",
            liters: full.liters ?? "",
            unit_price_jpy: full.unit_price_jpy ?? "",
            total_jpy: full.total_jpy ?? "",
            fuel_type: full.fuel_type || "",
            station_name: full.station_name || "",
            payment_method: full.payment_method || "",
            receipt_image_url: full.receipt_image_url || "",
            notes: full.notes || "",
        });
        setOpen(true);
    };

    // Auto-compute the missing one of (liters, unit_price, total) as the user types
    const formWithComputed = useMemo(() => {
        const f = { ...form };
        const L = parseFloat(f.liters);
        const U = parseFloat(f.unit_price_jpy);
        const T = parseFloat(f.total_jpy);
        if (Number.isFinite(L) && Number.isFinite(U) && !Number.isFinite(T)) {
            f._calc_total = Math.round(L * U);
        } else if (Number.isFinite(L) && Number.isFinite(T) && L > 0 && !Number.isFinite(U)) {
            f._calc_unit = Math.round((T / L) * 100) / 100;
        }
        return f;
    }, [form]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.vehicle_id || !form.refueled_at || !form.liters) {
            toast.error("車両 / 日付 / 給油量は必須です");
            return;
        }
        const payload = { ...form };
        for (const k of ["odometer_km", "total_jpy"]) {
            payload[k] = payload[k] === "" || payload[k] == null ? null : parseInt(payload[k]);
        }
        for (const k of ["liters", "unit_price_jpy"]) {
            payload[k] = payload[k] === "" || payload[k] == null ? null : parseFloat(payload[k]);
        }
        for (const k of Object.keys(payload)) {
            if (payload[k] === "") payload[k] = null;
        }
        const editing = payload.id;
        delete payload.id;
        try {
            if (editing) {
                await api.patch(`/fuel-logs/${editing}`, payload);
                toast.success("給油記録を更新しました");
            } else {
                const { data } = await api.post("/fuel-logs", payload);
                toast.success("給油記録を登録しました");
                if (data?._anomaly_alert) {
                    toast.warning(`⚠ ${data._anomaly_alert.title}`, { duration: 8000 });
                }
            }
            setOpen(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "登録失敗");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("この給油記録を削除しますか？")) return;
        try {
            await api.delete(`/fuel-logs/${id}`);
            toast.success("削除しました");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "削除失敗");
        }
    };

    const onReceiptFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const { dataUrl, bytes } = await compressImageTo1MB(file);
            setForm((f) => ({ ...f, receipt_image_url: dataUrl }));
            toast.success(`領収書を圧縮しました (${fmtKB(bytes)})`);
        } catch (err) {
            toast.error(err.message || "画像処理に失敗しました");
        }
    };

    return (
        <div className="space-y-4 sm:space-y-5" data-testid="fuel-page">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">// 燃料記録 / FUEL JOURNAL</p>
                    <h1 className="text-xl sm:text-[1.685rem] font-bold tracking-tight">
                        {isStaff ? "自分の給油記録" : "給油記録"}
                    </h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canImport && (
                        <Button variant="ghost" leftIcon={UploadSimple} onClick={() => setCsvOpen(true)} data-testid="fuel-csv-btn">CSV取込</Button>
                    )}
                    {canAnalytics && (
                        <Button variant="ghost" leftIcon={ChartLineUp} onClick={() => setTrendOpen(true)} data-testid="fuel-trend-btn">燃費トレンド</Button>
                    )}
                    {canCreate && (
                        <Button leftIcon={Plus} onClick={openNew} data-testid="fuel-new-btn">給油を記録</Button>
                    )}
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBox label="記録数 (30日)" value={`${summary.trips} 件`} testId="fuel-stat-trips" />
                <StatBox label="給油量合計" value={`${summary.liters?.toFixed(1) || 0} L`} testId="fuel-stat-liters" />
                <StatBox label="燃料費合計" value={`¥${(summary.cost || 0).toLocaleString()}`} testId="fuel-stat-cost" />
                <StatBox label="平均燃費" value={summary.avg_kmpl > 0 ? `${summary.avg_kmpl} km/L` : "—"} testId="fuel-stat-kmpl" highlight={summary.avg_kmpl >= 12} />
            </div>

            {/* Filters */}
            <div className="prc-card p-3 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                    <label className="prc-label-overline block mb-1">車両</label>
                    <select className="prc-input" value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)} data-testid="fuel-filter-vehicle">
                        <option value="">全車両</option>
                        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
                    </select>
                </div>
                <div>
                    <label className="prc-label-overline block mb-1">開始日</label>
                    <DatePickerYmd value={filterFrom} onChange={setFilterFrom} testid="fuel-filter-from" />
                </div>
                <div>
                    <label className="prc-label-overline block mb-1">終了日</label>
                    <DatePickerYmd value={filterTo} onChange={setFilterTo} testid="fuel-filter-to" />
                </div>
                <button type="button" className="prc-btn-ghost w-full" onClick={() => { setFilterVehicle(""); setFilterFrom(""); setFilterTo(""); }}>条件をクリア</button>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2" data-testid="fuel-list-mobile">
                {list.length === 0 ? (
                    <p className="prc-card p-8 text-center text-sm text-[var(--prc-muted)]">給油記録はまだありません</p>
                ) : (
                    list.map((f) => (
                        <div key={f.id} className="prc-card p-3" data-testid={`fuel-card-${f.id}`}>
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="font-mono text-xs">{fmtYmd(f.refueled_at, f.refueled_at)}{f.refueled_time ? ` ${f.refueled_time}` : ""}</span>
                                {f.km_per_liter && (
                                    <span className={`text-xs font-mono ${f.km_per_liter >= 12 ? "text-[var(--prc-green)]" : f.km_per_liter < 8 ? "text-rose-300" : "text-amber-300"}`}>
                                        {f.km_per_liter} km/L
                                    </span>
                                )}
                            </div>
                            <p className="text-sm font-semibold truncate">{f.vehicle_plate || "-"}</p>
                            <p className="text-xs text-[var(--prc-muted)] truncate">{f.driver_name || "-"} · {f.station_name || "—"}</p>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--prc-border)]">
                                <span className="text-xs font-mono">
                                    {f.liters} L
                                    {f.unit_price_jpy ? <span className="ml-2 text-[var(--prc-muted)]">@¥{f.unit_price_jpy}/L</span> : null}
                                    {f.total_jpy ? <span className="ml-2 text-[var(--prc-green)]">¥{f.total_jpy.toLocaleString()}</span> : null}
                                </span>
                                <div className="flex gap-1">
                                    {f.has_receipt && (
                                        <IconButton icon={Receipt} size="sm" tone="success" title="領収書を表示" onClick={() => openReceipt(f)} data-testid={`fuel-receipt-${f.id}`} />
                                    )}
                                    {canUpdate && <IconButton icon={PencilSimple} size="sm" title="編集" onClick={() => openEdit(f)} />}
                                    {canDelete && <IconButton icon={Trash} size="sm" tone="danger" title="削除" onClick={() => remove(f.id)} />}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop table */}
            <div className="prc-card overflow-x-auto hidden md:block">
                {list.length === 0 ? (
                    <p className="p-8 text-center text-sm text-[var(--prc-muted)]">給油記録はまだありません</p>
                ) : (
                    <table className="prc-table" data-testid="fuel-table">
                        <thead><tr>
                            <th>日付</th>
                            <th>車両</th>
                            {!isStaff && <th>運転者</th>}
                            <th className="text-right">L</th>
                            <th className="text-right">@¥/L</th>
                            <th className="text-right">合計</th>
                            <th className="text-right">km/L</th>
                            <th>支払</th>
                            <th>店舗</th>
                            <th></th>
                        </tr></thead>
                        <tbody>
                            {list.map((f) => (
                                <tr key={f.id} data-testid={`fuel-row-${f.id}`}>
                                    <td className="num text-xs">{fmtYmd(f.refueled_at, f.refueled_at)}</td>
                                    <td className="font-mono">{f.vehicle_plate}</td>
                                    {!isStaff && <td>{f.driver_name || "-"}</td>}
                                    <td className="num">{f.liters}</td>
                                    <td className="num text-xs">{f.unit_price_jpy ? `¥${f.unit_price_jpy}` : "—"}</td>
                                    <td className="num text-[var(--prc-green)]">{f.total_jpy ? `¥${f.total_jpy.toLocaleString()}` : "—"}</td>
                                    <td className={`num font-mono ${f.km_per_liter >= 12 ? "text-[var(--prc-green)]" : f.km_per_liter < 8 ? "text-rose-300" : ""}`}>
                                        {f.km_per_liter || "—"}
                                    </td>
                                    <td className="text-xs">{PAYMENT_OPTIONS.find(p => p.value === f.payment_method)?.label || "—"}</td>
                                    <td className="text-xs truncate max-w-[180px]">{f.station_name || "—"}</td>
                                    <td>
                                        <div className="flex justify-end gap-1">
                                            {f.has_receipt && (
                                                <Button variant="ghost" size="sm" leftIcon={Receipt} onClick={() => openReceipt(f)} data-testid={`fuel-receipt-${f.id}`}>領収書</Button>
                                            )}
                                            {canUpdate && <Button variant="ghost" size="sm" leftIcon={PencilSimple} onClick={() => openEdit(f)}>編集</Button>}
                                            {canDelete && <Button variant="ghost" size="sm" leftIcon={Trash} onClick={() => remove(f.id)} className="!text-rose-300">削除</Button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Form dialog */}
            {open && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
                    <form onSubmit={submit} className="prc-card p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="fuel-dialog">
                        <p className="prc-label-overline mb-1 !text-[var(--prc-green)]">// 給油記録</p>
                        <h2 className="text-lg sm:text-xl font-bold mb-4">{form.id ? "給油記録を編集" : "給油を記録"}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="車両 *" cols={2}>
                                <VehicleQrField
                                    value={form.vehicle_id}
                                    vehicles={vehicles}
                                    onPick={(v) => setForm({ ...form, vehicle_id: v.id })}
                                    required
                                    testid="fuel-form-vehicle"
                                />
                            </Field>
                            <Field label="給油日 *">
                                <DatePickerYmd value={form.refueled_at} onChange={(val) => setForm({ ...form, refueled_at: val })} required testid="fuel-form-date" />
                            </Field>
                            <Field label="時刻">
                                <input type="time" className="prc-input" value={form.refueled_time} onChange={(e) => setForm({ ...form, refueled_time: e.target.value })} />
                            </Field>
                            <Field label="走行距離 (km)">
                                <input type="number" className="prc-input" value={form.odometer_km} onChange={(e) => setForm({ ...form, odometer_km: e.target.value })} placeholder="オドメータ" />
                            </Field>
                            <Field label="給油量 (L) *">
                                <input type="number" step="0.01" required className="prc-input" value={form.liters} onChange={(e) => setForm({ ...form, liters: e.target.value })} data-testid="fuel-form-liters" />
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {QUICK_LITERS.map((q) => (
                                        <button key={q} type="button" onClick={() => setForm((f) => ({ ...f, liters: String(q) }))} className="px-2 py-1 text-xs font-mono border border-[var(--prc-border)] rounded-sm hover:border-[var(--prc-green)] hover:text-[var(--prc-green)]">
                                            {q} L
                                        </button>
                                    ))}
                                    <button type="button" onClick={() => setForm((f) => ({ ...f, liters: "満タン", notes: f.notes || "満タン" }))} className="px-2 py-1 text-xs font-mono border border-[var(--prc-border)] rounded-sm hover:border-[var(--prc-green)] hover:text-[var(--prc-green)]">
                                        満タン
                                    </button>
                                </div>
                            </Field>
                            <Field label="単価 (¥/L)">
                                <input type="number" step="0.01" className="prc-input" value={form.unit_price_jpy} onChange={(e) => setForm({ ...form, unit_price_jpy: e.target.value })} placeholder="例: 165" />
                                {formWithComputed._calc_unit && (
                                    <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">// 計算値: ¥{formWithComputed._calc_unit}/L</p>
                                )}
                            </Field>
                            <Field label="合計金額 (¥)">
                                <input type="number" className="prc-input" value={form.total_jpy} onChange={(e) => setForm({ ...form, total_jpy: e.target.value })} placeholder="自動計算可" />
                                {formWithComputed._calc_total && (
                                    <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">// 計算値: ¥{formWithComputed._calc_total.toLocaleString()}</p>
                                )}
                            </Field>
                            <Field label="燃料種別">
                                <select className="prc-input" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}>
                                    <option value="">選択してください</option>
                                    <option value="ガソリン">ガソリン (レギュラー)</option>
                                    <option value="ハイオク">ハイオク</option>
                                    <option value="軽油">軽油</option>
                                    <option value="LPG">LPG</option>
                                    <option value="電気">電気</option>
                                </select>
                            </Field>
                            <Field label="支払方法">
                                <select className="prc-input" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="fuel-form-payment">
                                    <option value="">選択してください</option>
                                    {PAYMENT_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </Field>
                            <Field label="店舗 / SS名" cols={2}>
                                <input className="prc-input" value={form.station_name} onChange={(e) => setForm({ ...form, station_name: e.target.value })} placeholder="ENEOS本社前" />
                            </Field>
                            <Field label="領収書写真" cols={2}>
                                <div className="flex items-center gap-2">
                                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={onReceiptFile} className="hidden" data-testid="fuel-form-receipt-input" />
                                    <button type="button" onClick={() => fileInputRef.current?.click()} className="prc-btn-ghost inline-flex items-center gap-2 text-xs">
                                        <Camera size={16} />{form.receipt_image_url ? "差し替え" : "撮影 / アップロード"}
                                    </button>
                                    {form.receipt_image_url && (
                                        <>
                                            <button type="button" onClick={() => setReceiptModal({ open: true, src: form.receipt_image_url, busy: false, log: { refueled_at: form.refueled_at, vehicle_plate: vehicles.find(v=>v.id===form.vehicle_id)?.plate_number, liters: form.liters, total_jpy: parseInt(form.total_jpy) || null } })} className="text-xs text-[var(--prc-green)] underline">プレビュー</button>
                                            <button type="button" onClick={() => setForm((f) => ({ ...f, receipt_image_url: "" }))} className="text-xs text-rose-300 inline-flex items-center gap-1"><X size={16} />削除</button>
                                        </>
                                    )}
                                </div>
                                {form.receipt_image_url && (
                                    <img src={form.receipt_image_url} alt="領収書" className="mt-2 max-h-32 rounded-sm border border-[var(--prc-border)]" />
                                )}
                            </Field>
                            <Field label="備考" cols={2}>
                                <textarea className="prc-input min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--prc-border)]">
                            <button type="button" onClick={() => { setOpen(false); setForm(emptyForm); }} className="prc-btn-ghost">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="fuel-form-submit">{form.id ? "更新" : "登録"}</button>
                        </div>
                    </form>
                </div>
            )}

            {csvOpen && <CsvImportDialog vehicles={vehicles} onClose={() => setCsvOpen(false)} onDone={() => { setCsvOpen(false); load(); }} />}
            {trendOpen && <TrendDialog vehicles={vehicles} onClose={() => setTrendOpen(false)} />}
            {receiptModal.open && (
                <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-3" onClick={() => setReceiptModal({ open: false, src: "", busy: false, log: null })} data-testid="fuel-receipt-modal">
                    <div className="prc-card p-3 w-full sm:max-w-3xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <p className="prc-label-overline mb-0 !text-[var(--prc-green)]">// 領収書</p>
                            <IconButton icon={X} size="sm" title="閉じる" onClick={() => setReceiptModal({ open: false, src: "", busy: false, log: null })} data-testid="fuel-receipt-close" />
                        </div>
                        {receiptModal.log && (
                            <p className="text-xs text-[var(--prc-muted)] font-mono mb-2">
                                {receiptModal.log.refueled_at} · {receiptModal.log.vehicle_plate} · {receiptModal.log.liters} L
                                {receiptModal.log.total_jpy ? ` · ¥${receiptModal.log.total_jpy.toLocaleString()}` : ""}
                            </p>
                        )}
                        <div className="flex-1 overflow-auto flex items-center justify-center bg-black/40 rounded-sm p-2 min-h-[200px]">
                            {receiptModal.busy ? (
                                <p className="text-xs text-[var(--prc-muted)]">読込中...</p>
                            ) : receiptModal.src ? (
                                <img src={receiptModal.src} alt="領収書" className="max-w-full max-h-[80vh] object-contain" data-testid="fuel-receipt-image" />
                            ) : null}
                        </div>
                        {receiptModal.src && (
                            <div className="flex justify-end mt-2 pt-2 border-t border-[var(--prc-border)]">
                                <a href={receiptModal.src} download={`receipt-${receiptModal.log?.id || "fuel"}.jpg`} className="prc-btn-ghost text-xs">画像を保存</a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Client-side image downscale + JPEG re-encode is now provided by the shared
// `lib/imageCompress` utility (used by `onReceiptFile` above).
//
function StatBox({ label, value, testId, highlight }) {
    return (
        <div className="prc-card p-4" data-testid={testId}>
            <p className="prc-label-overline">{label}</p>
            <p className={`text-xl sm:text-2xl font-bold mt-1 font-mono ${highlight ? "text-[var(--prc-green)]" : ""}`}>{value}</p>
        </div>
    );
}

function Field({ label, cols = 1, children }) {
    return (
        <div className={cols === 2 ? "sm:col-span-2" : ""}>
            <label className="prc-label-overline block mb-1">{label}</label>
            {children}
        </div>
    );
}

function CsvImportDialog({ onClose, onDone }) {
    const [csv, setCsv] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async () => {
        if (!csv.trim()) { toast.error("CSVを貼り付けてください"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/fuel-logs/import-csv", { csv_text: csv, default_payment_method: "corporate_card" });
            setResult(data);
            if (data.inserted > 0) toast.success(`${data.inserted}件取り込みました`);
            if (data.skipped > 0) toast.warning(`${data.skipped}件スキップ`);
            if (data.inserted > 0) setTimeout(onDone, 1200);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "取込失敗");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <div className="prc-card p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="fuel-csv-dialog">
                <p className="prc-label-overline mb-1 !text-amber-300">// CSV取込 / FUEL CARD STATEMENT</p>
                <h2 className="text-lg font-bold mb-2">法人カード明細を一括取込</h2>
                <p className="text-xs text-[var(--prc-muted)] mb-3 font-mono">// 必須列: 日付・車両（ナンバー）・給油量(L)。任意: 単価・合計金額・店舗</p>
                <textarea
                    className="prc-input min-h-[200px] font-mono text-xs"
                    placeholder="日付,車両,給油量(L),単価,合計金額,店舗
2026/04/10,TEST999,30.0,148,4440,SS品川"
                    value={csv}
                    onChange={(e) => setCsv(e.target.value)}
                    data-testid="fuel-csv-textarea"
                />
                {result && (
                    <div className="mt-3 prc-card p-3 text-xs">
                        <p>取込: <span className="text-[var(--prc-green)] font-bold">{result.inserted} 件</span> / スキップ: <span className="text-amber-300">{result.skipped} 件</span></p>
                        {result.errors?.length > 0 && (
                            <ul className="mt-2 space-y-0.5">
                                {result.errors.map((e, i) => <li key={i} className="text-rose-300 font-mono text-[10px]">{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[var(--prc-border)]">
                    <button onClick={onClose} className="prc-btn-ghost">閉じる</button>
                    <button onClick={submit} disabled={busy} className="prc-btn-primary" data-testid="fuel-csv-submit">{busy ? "取込中..." : "取込実行"}</button>
                </div>
            </div>
        </div>
    );
}

function TrendDialog({ vehicles, onClose }) {
    const [vid, setVid] = useState(vehicles[0]?.id || "");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!vid) return;
        setBusy(true);
        api.get(`/fuel-logs/trend/vehicle/${vid}?months=6`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setBusy(false));
    }, [vid]);

    const max = useMemo(() => Math.max(0, ...(data?.monthly?.map((m) => m.avg_kmpl) || [0])), [data]);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <div className="prc-card p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="fuel-trend-dialog">
                <p className="prc-label-overline mb-1 !text-[var(--prc-green)]">// 燃費トレンド</p>
                <h2 className="text-lg font-bold mb-3">月別 燃費 / 燃料費</h2>
                <select className="prc-input mb-3" value={vid} onChange={(e) => setVid(e.target.value)}>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
                </select>
                {busy ? (
                    <p className="text-xs text-[var(--prc-muted)]">読込中...</p>
                ) : data?.monthly?.length ? (
                    <div className="space-y-2">
                        {data.monthly.map((m) => (
                            <div key={m.month} className="prc-card p-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-mono text-xs">{m.month}</span>
                                    <span className={`text-xs font-mono ${m.avg_kmpl >= 12 ? "text-[var(--prc-green)]" : m.avg_kmpl < 8 ? "text-rose-300" : "text-amber-300"}`}>{m.avg_kmpl} km/L</span>
                                </div>
                                <div className="h-2 bg-[var(--prc-surface-2)] rounded-sm overflow-hidden">
                                    <div className="h-full bg-[var(--prc-green)]" style={{ width: max ? `${(m.avg_kmpl / max) * 100}%` : 0 }} />
                                </div>
                                <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">{m.liters.toFixed(1)} L · ¥{m.cost.toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-[var(--prc-muted)]">データがありません</p>
                )}
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[var(--prc-border)]">
                    <button onClick={onClose} className="prc-btn-ghost">閉じる</button>
                </div>
            </div>
        </div>
    );
}
